import { useState, useMemo, useEffect } from 'react'
import { useAuthStore, usePlayerStore } from '../stores'
import { Player } from '../types'
import { useProStatus } from '../utils/useProStatus'
import {
  User, Save, X, Camera, Shield, ShieldCheck, FileText,
  Trophy, Target, ChevronDown,
  ChevronUp, Loader2, CheckCircle2, AlertCircle, Edit3, Image as ImageIcon
} from 'lucide-react'
import ImageUpload from './ImageUpload'

const POSITIONS = ['GK', 'CB', 'RB', 'LB', 'RWB', 'LWB', 'CDM', 'CM', 'CAM', 'LW', 'RW', 'SS', 'CF']

const NIGERIAN_STATES = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
  'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe', 'Imo',
  'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nassarawa',
  'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba',
  'Yobe', 'Zamfara'
]

const FOOT_OPTIONS = ['Left', 'Right', 'Both']

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">{label}</label>
      {children}
    </div>
  )
}

function ProfileInput({ value, onChange, disabled, placeholder, type = 'text' }: any) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold disabled:opacity-50"
    />
  )
}

function ProfileTextArea({ value, onChange, disabled, placeholder, rows = 3 }: any) {
  return (
    <textarea
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold disabled:opacity-50 resize-none"
    />
  )
}

function ProfileSelect({ value, onChange, disabled, options }: any) {
  return (
    <select
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold disabled:opacity-50"
    >
      <option value="" className="bg-gray-900">Select...</option>
      {options.map((opt: string) => (
        <option key={opt} value={opt} className="bg-gray-900">{opt}</option>
      ))}
    </select>
  )
}

function PlayerDashboardProfile() {
  const { user } = useAuthStore()
  const { players, updatePlayer, fetchPlayers } = usePlayerStore()
  const { isPro } = useProStatus()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [creatingProfile, setCreatingProfile] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    personal: true,
    football: true,
    biography: true,
    photos: true,
    verification: true,
  })

  const currentPlayer = useMemo(() => {
    if (!user) return null
    return players.find(p =>
      p.scout?.toLowerCase() === user.email?.toLowerCase() ||
      p.email?.toLowerCase() === user.email?.toLowerCase() ||
      p.name?.toLowerCase() === user.name?.toLowerCase()
    ) || null
  }, [user, players])

  useEffect(() => {
    if (!user || currentPlayer || creatingProfile) return
    if (user.role !== 'player') return

    async function createMissingProfile() {
      setCreatingProfile(true)
      try {
        const { apiClient } = await import('../utils/api')
        const newPlayer = await apiClient.createPlayer({
          name: user!.name || user!.email || 'New Player',
          age: 18,
          positions: [],
          main_position: '',
          club: 'Free Agent',
          league: '',
          competitions: [],
          state: '',
          lga: '',
          foot: 'Right',
          height: 0,
          weight: 0,
          goals: 0,
          assists: 0,
          apps: 0,
          shots: 0,
          kp: 0,
          dr: 0,
          du: 0,
          mins: 0,
          potential: 3,
          technical: 5,
          physical: 5,
          mental: 5,
          notes: '',
          match_desc: '',
          scout: user!.email || '',
          photo_url: null,
          youtube: null,
          is_verified: false,
          is_public: true,
        })
        if (newPlayer) {
          usePlayerStore.setState((state) => ({
            players: [newPlayer, ...state.players]
          }))
        }
        await fetchPlayers()
      } catch (e) {
        console.error('Auto-create player profile failed:', e)
      } finally {
        setCreatingProfile(false)
      }
    }
    createMissingProfile()
  }, [user, currentPlayer, creatingProfile, fetchPlayers])

  const [formData, setFormData] = useState<Partial<Player>>({})
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const [fullBodyPhotoUrl, setFullBodyPhotoUrl] = useState<string | null>(null)
  const [actionPhotosUrls, setActionPhotosUrls] = useState<string[]>([])
  const [identityDocUrl, setIdentityDocUrl] = useState<string | null>(null)

  const startEditing = () => {
    if (!currentPlayer) return
    setFormData({
      name: currentPlayer.name,
      age: currentPlayer.age,
      date_of_birth: currentPlayer.date_of_birth || '',
      nationality: currentPlayer.nationality || '',
      state: currentPlayer.state,
      city: currentPlayer.city || '',
      lga: currentPlayer.lga,
      height: currentPlayer.height,
      weight: currentPlayer.weight,
      foot: currentPlayer.foot || '',
      main_position: currentPlayer.main_position,
      positions: currentPlayer.positions || [],
      club: currentPlayer.club,
      affiliation_type: currentPlayer.affiliation_type || undefined,
      has_agent: currentPlayer.has_agent ?? false,
      agent_name: currentPlayer.agent_name || '',
      agent_contact: currentPlayer.agent_contact || '',
      league: currentPlayer.league,
      jersey_number: currentPlayer.jersey_number,
      career_history: currentPlayer.career_history || [],
      biography: currentPlayer.biography || '',
      strengths: currentPlayer.strengths || '',
      career_goals: currentPlayer.career_goals || '',
      notes: currentPlayer.notes,
    })
    setPhotoUrl(currentPlayer.photo_url || null)
    setFullBodyPhotoUrl(currentPlayer.full_body_photo_url || null)
    setActionPhotosUrls(currentPlayer.action_photos_urls || [])
    setIdentityDocUrl(currentPlayer.identity_document_url || null)
    setEditing(true)
    setError('')
    setSuccess('')
  }

  const cancelEditing = () => {
    setEditing(false)
    setError('')
  }

  const handleSave = async () => {
    if (!currentPlayer) return
    setSaving(true)
    setError('')

    try {
      await updatePlayer(currentPlayer.id, {
        name: formData.name,
        age: formData.age,
        date_of_birth: formData.date_of_birth,
        nationality: formData.nationality,
        state: formData.state,
        city: formData.city,
        lga: formData.lga,
        height: formData.height,
        weight: formData.weight,
        foot: formData.foot,
        main_position: formData.main_position,
        positions: formData.positions,
        club: formData.club,
        affiliation_type: formData.affiliation_type || undefined,
        has_agent: formData.has_agent ?? false,
        agent_name: formData.agent_name || undefined,
        agent_contact: formData.agent_contact || undefined,
        league: formData.league,
        jersey_number: formData.jersey_number,
        career_history: formData.career_history,
        biography: formData.biography,
        strengths: formData.strengths,
        career_goals: formData.career_goals,
        photo_url: photoUrl,
        full_body_photo_url: fullBodyPhotoUrl,
        action_photos_urls: actionPhotosUrls,
        identity_document_url: identityDocUrl,
      })
      setEditing(false)
      setSuccess('Profile updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const profileCompletion = useMemo(() => {
    if (!currentPlayer) return 0
    const fields = [
      currentPlayer.name,
      currentPlayer.age,
      currentPlayer.date_of_birth,
      currentPlayer.nationality,
      currentPlayer.main_position || currentPlayer.positions?.length,
      currentPlayer.club,
      currentPlayer.league,
      currentPlayer.height,
      currentPlayer.weight,
      currentPlayer.photo_url,
      currentPlayer.state,
      currentPlayer.city,
      currentPlayer.lga,
      currentPlayer.foot,
      currentPlayer.biography,
      currentPlayer.strengths,
      currentPlayer.career_goals,
      currentPlayer.goals > 0 || currentPlayer.assists > 0,
    ]
    const filled = fields.filter(Boolean).length
    return Math.round((filled / fields.length) * 100)
  }, [currentPlayer])

  const yearsOfExperience = useMemo(() => {
    if (!currentPlayer?.career_history?.length) return 0
    return currentPlayer.career_history.reduce((acc, entry) => {
      const match = entry.years?.match(/(\d{4})\s*[-–]\s*(\d{4}|Present|Current)/i)
      if (match) {
        const start = parseInt(match[1])
        const end = match[2].toLowerCase() === 'present' || match[2].toLowerCase() === 'current'
          ? new Date().getFullYear()
          : parseInt(match[2])
        return acc + Math.max(0, end - start)
      }
      return acc
    }, 0)
  }, [currentPlayer?.career_history])

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (!currentPlayer) {
    if (creatingProfile) {
      return (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center">
          <Loader2 className="mx-auto text-green-500 mb-4 animate-spin" size={48} />
          <h3 className="text-lg font-semibold text-white">Creating your player profile...</h3>
          <p className="text-gray-400 mt-2">Setting up your account.</p>
        </div>
      )
    }
    return (
      <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center">
        <User className="mx-auto text-gray-500 mb-4" size={48} />
        <h3 className="text-lg font-semibold text-white">No Profile Found</h3>
        <p className="text-gray-400 mt-2">Your player profile hasn't been created yet. Contact an admin or scout to get started.</p>
      </div>
    )
  }

  const player = currentPlayer

  const SectionHeader = ({ title, icon: Icon, section }: { title: string; icon: any; section: string }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-[#0d1117] rounded-xl border border-gray-800 hover:border-green-500/20 transition"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
          <Icon size={16} className="text-green-500" />
        </div>
        <h3 className="text-sm font-bold text-white uppercase tracking-wider">{title}</h3>
      </div>
      {expandedSections[section] ? (
        <ChevronUp size={16} className="text-gray-500" />
      ) : (
        <ChevronDown size={16} className="text-gray-500" />
      )}
    </button>
  )

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Profile Completion Bar */}
      <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Profile Completion</h3>
          <span className="text-2xl font-black text-green-400">{profileCompletion}%</span>
        </div>
        <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-500"
            style={{ width: `${profileCompletion}%` }}
          />
        </div>
        <p className="text-[10px] text-gray-500 mt-2 uppercase tracking-widest">
          {profileCompletion < 50 ? 'Complete your profile to attract scouts' :
           profileCompletion < 80 ? 'Looking good! Add more details' :
           'Your profile is well-optimized'}
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-700/30 rounded-xl text-green-400 text-xs font-bold">
          <CheckCircle2 size={16} />
          {success}
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-700/30 rounded-xl text-red-400 text-xs font-bold">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Edit/Save Buttons */}
      <div className="flex justify-end gap-3">
        {editing ? (
          <>
            <button
              onClick={cancelEditing}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-gray-400 hover:text-white transition text-xs font-bold uppercase tracking-widest"
            >
              <X size={14} />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 border border-green-500 rounded-xl text-black hover:bg-green-400 transition text-xs font-bold uppercase tracking-widest disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        ) : (
          <button
            onClick={startEditing}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400 hover:bg-green-500/20 transition text-xs font-bold uppercase tracking-widest"
          >
            <Edit3 size={14} />
            Edit Profile
          </button>
        )}
      </div>

      {/* Personal Information */}
      <div className="space-y-3">
        <SectionHeader title="Personal Information" icon={User} section="personal" />
        {expandedSections.personal && (
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full Name">
                <ProfileInput
                  value={editing ? formData.name : player.name}
                  onChange={(e: any) => updateFormData('name', e.target.value)}
                  disabled={!editing}
                  placeholder="Enter full name"
                />
              </Field>
              <Field label="Date of Birth">
                <ProfileInput
                  type="date"
                  value={editing ? formData.date_of_birth : player.date_of_birth}
                  onChange={(e: any) => updateFormData('date_of_birth', e.target.value)}
                  disabled={!editing}
                />
              </Field>
              <Field label="Nationality">
                <ProfileInput
                  value={editing ? formData.nationality : player.nationality}
                  onChange={(e: any) => updateFormData('nationality', e.target.value)}
                  disabled={!editing}
                  placeholder="e.g. Nigerian"
                />
              </Field>
              <Field label="State">
                <ProfileSelect
                  value={editing ? formData.state : player.state}
                  onChange={(e: any) => updateFormData('state', e.target.value)}
                  disabled={!editing}
                  options={NIGERIAN_STATES}
                />
              </Field>
              <Field label="City">
                <ProfileInput
                  value={editing ? formData.city : player.city}
                  onChange={(e: any) => updateFormData('city', e.target.value)}
                  disabled={!editing}
                  placeholder="e.g. Lagos"
                />
              </Field>
              <Field label="Height (cm)">
                <ProfileInput
                  type="number"
                  value={editing ? formData.height : player.height}
                  onChange={(e: any) => updateFormData('height', parseInt(e.target.value) || undefined)}
                  disabled={!editing}
                  placeholder="e.g. 180"
                />
              </Field>
              <Field label="Weight (kg)">
                <ProfileInput
                  type="number"
                  value={editing ? formData.weight : player.weight}
                  onChange={(e: any) => updateFormData('weight', parseInt(e.target.value) || undefined)}
                  disabled={!editing}
                  placeholder="e.g. 75"
                />
              </Field>
            </div>
          </div>
        )}
      </div>

      {/* Football Information */}
      <div className="space-y-3">
        <SectionHeader title="Football Information" icon={Target} section="football" />
        {expandedSections.football && (
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Preferred Foot">
                <ProfileSelect
                  value={editing ? formData.foot : player.foot}
                  onChange={(e: any) => updateFormData('foot', e.target.value)}
                  disabled={!editing}
                  options={FOOT_OPTIONS}
                />
              </Field>
              <Field label="Primary Position">
                <ProfileSelect
                  value={editing ? formData.main_position : player.main_position}
                  onChange={(e: any) => updateFormData('main_position', e.target.value)}
                  disabled={!editing}
                  options={POSITIONS}
                />
              </Field>
              <Field label="Secondary Position">
                <ProfileInput
                  value={editing ? (formData.positions?.[1] || '') : (player.positions?.[1] || '')}
                  onChange={(e: any) => {
                    const pos = [...(formData.positions || [])]
                    if (e.target.value) pos[1] = e.target.value
                    else pos.splice(1, 1)
                    updateFormData('positions', pos)
                  }}
                  disabled={!editing}
                  placeholder="e.g. CAM"
                />
              </Field>
              <Field label="Current Club">
                <ProfileInput
                  value={editing ? formData.club : player.club}
                  onChange={(e: any) => updateFormData('club', e.target.value)}
                  disabled={!editing}
                  placeholder="e.g. FC Unity"
                />
              </Field>
              <Field label="Affiliation">
                <select
                  value={editing ? (formData.affiliation_type || '') : (player.affiliation_type || '')}
                  onChange={(e: any) => updateFormData('affiliation_type', e.target.value || undefined)}
                  disabled={!editing}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white text-sm font-bold focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition disabled:opacity-50"
                >
                  <option value="">Select...</option>
                  <option value="club">Club</option>
                  <option value="academy">Academy</option>
                  <option value="free_agent">Free Agent</option>
                </select>
              </Field>
              <Field label="Has Agent/Manager">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => editing && updateFormData('has_agent', true)}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition border ${
                      (editing ? formData.has_agent : player.has_agent) === true
                        ? 'bg-green-500/10 border-green-500/30 text-green-400'
                        : 'bg-white/5 border-white/5 text-gray-500 hover:text-white'
                    }`}
                    disabled={!editing}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => editing && updateFormData('has_agent', false)}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition border ${
                      (editing ? formData.has_agent : player.has_agent) === false
                        ? 'bg-red-500/10 border-red-500/30 text-red-400'
                        : 'bg-white/5 border-white/5 text-gray-500 hover:text-white'
                    }`}
                    disabled={!editing}
                  >
                    No
                  </button>
                </div>
              </Field>
              {(editing ? formData.has_agent : player.has_agent) && (
                <>
                  <Field label="Agent/Manager Name">
                    <ProfileInput
                      value={editing ? formData.agent_name : player.agent_name}
                      onChange={(e: any) => updateFormData('agent_name', e.target.value)}
                      disabled={!editing}
                      placeholder="e.g. John Doe"
                    />
                  </Field>
                  <Field label="Agent Contact">
                    <ProfileInput
                      value={editing ? formData.agent_contact : player.agent_contact}
                      onChange={(e: any) => updateFormData('agent_contact', e.target.value)}
                      disabled={!editing}
                      placeholder="e.g. +234 801 234 5678"
                    />
                  </Field>
                </>
              )}
              <Field label="Jersey Number">
                <ProfileInput
                  type="number"
                  value={editing ? formData.jersey_number : player.jersey_number}
                  onChange={(e: any) => updateFormData('jersey_number', parseInt(e.target.value) || undefined)}
                  disabled={!editing}
                  placeholder="e.g. 10"
                />
              </Field>
              <Field label="Years of Experience">
                <ProfileInput
                  value={yearsOfExperience > 0 ? `${yearsOfExperience} years` : 'N/A'}
                  disabled
                />
              </Field>
            </div>

            {/* Previous Clubs */}
            <div className="mt-4">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Previous Clubs</label>
              {player.career_history && player.career_history.length > 0 ? (
                <div className="space-y-2">
                  {player.career_history.map((entry, i) => (
                    <div key={entry.id || i} className="flex items-center gap-3 p-3 bg-[#0d1117] rounded-xl border border-gray-800">
                      <Trophy size={14} className="text-green-500 flex-shrink-0" />
                      <span className="text-sm font-bold text-white">{entry.club}</span>
                      <span className="text-[10px] text-gray-500 uppercase tracking-widest">{entry.years}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic">No career history recorded yet.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Biography */}
      <div className="space-y-3">
        <SectionHeader title="Biography" icon={FileText} section="biography" />
        {expandedSections.biography && (
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 space-y-4">
            <Field label="Football Story">
              <ProfileTextArea
                value={editing ? formData.biography : player.biography}
                onChange={(e: any) => updateFormData('biography', e.target.value)}
                disabled={!editing}
                placeholder="Tell your football journey..."
                rows={4}
              />
            </Field>
            <Field label="Strengths">
              <ProfileTextArea
                value={editing ? formData.strengths : player.strengths}
                onChange={(e: any) => updateFormData('strengths', e.target.value)}
                disabled={!editing}
                placeholder="What are your key strengths as a player?"
                rows={3}
              />
            </Field>
            <Field label="Career Goals">
              <ProfileTextArea
                value={editing ? formData.career_goals : player.career_goals}
                onChange={(e: any) => updateFormData('career_goals', e.target.value)}
                disabled={!editing}
                placeholder="What are your career aspirations?"
                rows={3}
              />
            </Field>
          </div>
        )}
      </div>

      {/* Photos */}
      <div className="space-y-3">
        <SectionHeader title="Photos" icon={Camera} section="photos" />
        {expandedSections.photos && (
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 space-y-6">
            {/* Profile Photo */}
            <div>
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Profile Photo</label>
              <ImageUpload
                onUpload={(url) => setPhotoUrl(url)}
                currentUrl={photoUrl}
              />
            </div>

            {/* Full Body Photo */}
            <div>
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Full Body Photo</label>
              <ImageUpload
                onUpload={(url) => setFullBodyPhotoUrl(url)}
                currentUrl={fullBodyPhotoUrl}
              />
            </div>

            {/* Action Photos */}
            <div>
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Action Photos</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {actionPhotosUrls.map((url, i) => (
                  <div key={i} className="relative group">
                    <img src={url} alt={`Action ${i + 1}`} className="w-full h-32 object-cover rounded-xl border border-gray-800" />
                    {editing && (
                      <button
                        onClick={() => setActionPhotosUrls(prev => prev.filter((_, j) => j !== i))}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      >
                        <X size={12} className="text-white" />
                      </button>
                    )}
                  </div>
                ))}
                {editing && (isPro || (photoUrl ? 1 : 0) + (fullBodyPhotoUrl ? 1 : 0) + actionPhotosUrls.length < 5) && actionPhotosUrls.length < 6 && (
                  <label className="h-32 border-2 border-dashed border-gray-800 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-green-500/30 transition">
                    <ImageIcon size={20} className="text-gray-500 mb-1" />
                    <span className="text-[10px] text-gray-500 uppercase tracking-widest">Add Photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]
                        if (!file) return
                        try {
                          const { uploadToCloudinary } = await import('../utils/cloudinary')
                          const data = await uploadToCloudinary(file, 'players/action')
                          setActionPhotosUrls(prev => [...prev, data.secure_url])
                        } catch (err) {
                          console.error('Upload failed:', err)
                        }
                      }}
                    />
                  </label>
                )}
              </div>
              <p className="text-[10px] text-gray-600 mt-2 uppercase tracking-widest">Up to 6 action photos</p>
            </div>
          </div>
        )}
      </div>

      {/* Verification */}
      <div className="space-y-3">
        <SectionHeader title="Verification" icon={ShieldCheck} section="verification" />
        {expandedSections.verification && (
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 space-y-4">
            {/* Identity Document */}
            <div className="flex items-center justify-between p-4 bg-[#0d1117] rounded-xl border border-gray-800">
              <div className="flex items-center gap-3">
                <FileText size={16} className="text-blue-500" />
                <div>
                  <p className="text-sm font-bold text-white">Identity Document</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-widest">
                    {player.identity_document_url ? 'Uploaded' : 'Not uploaded'}
                  </p>
                </div>
              </div>
              {editing && (
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      try {
                        const { uploadToCloudinary } = await import('../utils/cloudinary')
                        const data = await uploadToCloudinary(file, 'players/identity')
                        setIdentityDocUrl(data.secure_url)
                      } catch (err) {
                        console.error('Upload failed:', err)
                      }
                    }}
                  />
                  <span className="text-xs text-green-400 hover:text-green-300 font-bold uppercase tracking-widest cursor-pointer">
                    Upload
                  </span>
                </label>
              )}
            </div>

            {/* Club Verification */}
            <div className="flex items-center justify-between p-4 bg-[#0d1117] rounded-xl border border-gray-800">
              <div className="flex items-center gap-3">
                {player.club_verified ? (
                  <ShieldCheck size={16} className="text-green-500" />
                ) : (
                  <Shield size={16} className="text-gray-500" />
                )}
                <div>
                  <p className="text-sm font-bold text-white">Club Verification</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-widest">
                    {player.club_verified ? 'Verified by club' : 'Pending verification'}
                  </p>
                </div>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full ${
                player.club_verified
                  ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                  : 'bg-gray-800 text-gray-500 border border-gray-700'
              }`}>
                {player.club_verified ? 'Verified' : 'Pending'}
              </span>
            </div>

            {/* Academy Verification */}
            <div className="flex items-center justify-between p-4 bg-[#0d1117] rounded-xl border border-gray-800">
              <div className="flex items-center gap-3">
                {player.academy_verified ? (
                  <ShieldCheck size={16} className="text-green-500" />
                ) : (
                  <Shield size={16} className="text-gray-500" />
                )}
                <div>
                  <p className="text-sm font-bold text-white">Academy Verification</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-widest">
                    {player.academy_verified ? 'Verified by academy' : 'Pending verification'}
                  </p>
                </div>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full ${
                player.academy_verified
                  ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                  : 'bg-gray-800 text-gray-500 border border-gray-700'
              }`}>
                {player.academy_verified ? 'Verified' : 'Pending'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlayerDashboardProfile
